// Global type definitions for the BIM Viewer application

export interface Coordinates {
  x: number;
  y: number;
  z?: number;
  latitude?: number;
  longitude?: number;
}

export interface POI {
  id: string;
  name: string;
  category: POICategory;
  coordinates: Coordinates;
  // New coordinate fields for different display systems
  model_coordinates?: Coordinates; // Original coordinates from IFC model
  normalized_2d_coordinates?: Coordinates; // Y/Z swapped coordinates for 2D viewer/minimap
  properties: {
    description?: string;
    type: string;
    subCategory?: string;
    originalCategory?: string;
    system?: string;
    location?: string;
    status?: string;
    metadata: Record<string, any>;
    ifcElementGuid?: string; // IFC element GUID for direct element selection
    ifcElementExpressId?: number; // IFC element expressID for direct element selection
    // User-assigned system hierarchy from "Set Systems" feature (takes priority over IFC categories)
    systemCategory?: string;     // Top-level category assigned by user
    systemSubcategory?: string;  // Second-level subcategory assigned by user
    systemName?: string;         // Third-level system name assigned by user
  };
  visualization: {
    icon: string;
    color: string;
    size: number;
    visibility: boolean;
  };
  isCluster?: false; // Discriminator for type narrowing
}

// Cluster of multiple POIs of the same category
export interface ClusterPOI {
  id: string;
  name: string;
  category: POICategory;
  coordinates: Coordinates; // Center point of cluster
  isCluster: true; // Discriminator for type narrowing
  clusterCount: number; // Number of POIs in cluster
  clusterCategory: string; // Original category name for display
  containedPOIs: POI[]; // All POIs in this cluster
  visualization: {
    icon: string;
    color: string;
    size: number;
    visibility: boolean;
  };
}

// Union type for rendering
export type POIOrCluster = POI | ClusterPOI;

export enum POICategory {
  STRUCTURE = "structure",
  ROOM = "room",
  SYSTEM = "system",
  SAFETY = "safety",
  UTILITY = "utility"
}

export type ViewMode = '3D' | '2D' | 'Panorama';

export interface SearchResult {
  id: string;
  name: string;
  type: 'system' | 'location' | 'poi';
  coordinates?: Coordinates;
  relevanceScore?: number;
  matchType?: "name" | "description" | "system" | "location" | "manufacturer";
  displayInfo?: {
    primaryHierarchy: "system" | "location";
    systemContext: string;
    locationContext: string;
    statusIndicator: string;
    matchHighlight: string;
  };
}

export interface Layer {
  id: string;
  name: string;
  type: string;
  visible: boolean;
  opacity: number;
  data?: any;
}

export interface Measurement {
  id: string;
  type: 'linear' | 'area' | 'angle' | 'volume';
  points: Coordinates[];
  value: number;
  unit: string;
  label?: string;
  visible: boolean;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  permissions: Permission[];
}

export enum UserRole {
  ADMINISTRATOR = "administrator",
  PROJECT_MANAGER = "project_manager",
  DESIGNER_ENGINEER = "designer_engineer",
  VIEWER = "viewer",
  CONTRACTOR_VENDOR = "contractor_vendor"
}

export interface Permission {
  type: 'spatial' | 'system' | 'data' | 'temporal';
  scope: string[];
  actions: string[];
}

export interface AppConfig {
  esri: {
    appId: string;
    portalUrl: string;
    clientId: string;
    apiKey?: string;
  };
  viewer: {
    defaultMode: ViewMode;
    enableMeasurement: boolean;
    enablePOI: boolean;
    maxFileSize: number;
  };
  ui: {
    theme: 'light' | 'dark';
    language: string;
    units: 'metric' | 'imperial';
  };
  performance: {
    enableLOD: boolean;
    enableClustering: boolean;
    cacheSize: number;
  };
  services: {
    modelStorageUrl: string;
    conversionServiceUrl: string;
  };
}

// Event types for the application
export interface ViewerEvents {
  'model-loaded': (modelId: string) => void;
  'view-mode-changed': (mode: ViewMode) => void;
  'poi-selected': (poi: POI) => void;
  'poi-added': (poi: POI) => void;
  'poi-updated': (poi: POI) => void;
  'coordinate-transformed': (coords: Coordinates) => void;
  'measurement-added': (measurement: Measurement) => void;
  'layer-visibility-changed': (layerId: string, visible: boolean) => void;
}

// Widget state interfaces
export interface POIManagerState {
  pois: POI[];
  selectedPOI?: POI;
  categories: POICategory[];
  filters: {
    category?: POICategory;
    system?: string;
    location?: string;
  };
}

export interface SearchBarState {
  searchTerm: string;
  results: SearchResult[];
  isSearching: boolean;
  selectedResult?: SearchResult;
}

// E57 Panorama types with enhanced metadata support
export interface E57PanoramaData {
  imageUrl: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number; w: number };
  scanIndex?: number;
  acquisitionDateTime?: string;

  // Enhanced metadata from real-time extraction
  metadata?: {
    fileOffset: number;
    length: number;
    angles: {
      yaw: number;
      pitch: number;
      roll: number;
      yawRadians: number;
      pitchRadians: number;
      rollRadians: number;
    };
    camera: {
      focalLength?: number;
      imageWidth?: number;
      imageHeight?: number;
      principalPointX?: number;
      principalPointY?: number;
      pixelWidth?: number;
      pixelHeight?: number;
    };
    setup: {
      id: string;
      name: string;
      setupPosition?: {
        x: number;
        y: number;
        z: number;
      };
    };
    imageIndex: number;
  };

  // Legacy hierarchical metadata for Y28-003 structure (backwards compatibility)
  setupName?: string;
  setupNumber?: number;
  angleIndex?: number;
  angleFile?: string;
  dateCode?: string;
}

// Asset Management Types
export enum AssetFileType {
  IFC = 'ifc',
  DWG = '2d',
  E57 = 'e57',
  PDF = 'pdf',
  RCP = 'rcp',
  RCS = 'rcs'
}

export interface AssetFile {
  id: string;
  type: AssetFileType;
  fileName: string;
  filePath: string;
  fileSize: number;
  uploadDate: string;
  isUploaded: boolean;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  metadata?: {
    [key: string]: any;
  };
  panoramaCount?: number;
  totalPanoramaCount?: number;
  sourceFileId?: number | string;
}

export interface Asset {
  id: string;
  name: string;
  description?: string;
  files: {
    ifc?: AssetFile; // 3D model
    rvt?: AssetFile; // Primary/default local RVT
    rvtFiles?: AssetFile[]; // All local RVT uploads
    dwg?: AssetFile; // 2D model
    e57?: AssetFile; // Panorama file
    e57Sources?: AssetFile[];
    [key: string]: AssetFile | AssetFile[] | undefined;
  };
  createdDate: string;
  isActive: boolean;
  panoramaData?: E57PanoramaData[]; // Extracted panoramic images
}

export interface AssetUploadProgress {
  assetId: string;
  fileType: AssetFileType;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  message?: string;
}

export interface CreateAssetRequest {
  name: string;
  description?: string;
}
